import classNames from 'classnames';
import Button from 'components/gruenland/atoms/button/Button';
import Link from 'components/gruenland/atoms/link';
import Typography from 'components/gruenland/atoms/typography';
import { AccountContext } from 'context/account';
import { UpdateAccount } from 'frontastic/hooks/useAccount/types';
import toAccountUrl from 'helpers/account/toAccountUrl';
import { useFormat } from 'helpers/hooks/useFormat';
import { useParams, useRouter } from 'next/navigation';
import { useCallback, useContext, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Account } from 'types/entity/account';
import { ShippingMethod } from 'types/entity/cart';
import { Order, ShipmentState } from 'types/entity/order';
import Addresses from './sections/addresses';
import AddressForm from './sections/addresses/address-form';
import DashboardSection from './sections/dashboard/DashboardSection';
import OrderPage from './sections/orders/order-page/OrderPage';
import Orders from './sections/orders/Orders';
import MyAccount from './sections/profile';
import ChangePasswordForm from './sections/profile/forms/change-password-form';
import DeleteAccountForm from './sections/profile/forms/delete-account-form';
import PersonalInfoForm from './sections/profile/forms/personal-info-form';
import OrderReturnButton from './sections/orders/order-page/OrderReturnButton';

export interface AccountTab {
  name: string;
  href: string;
  isActive: boolean;
}
export interface FAQ {
  question: string;
  answer: string;
}

export type AccountDetailsProps = {
  hash: string;
  id: string | null;
  orders: Order[];
  shippingMethods: ShippingMethod[];
  ordersLoading?: boolean;
  account?: Account;
  changePassword?: (oldPassword: string, newPassword: string) => Promise<Account>;
  deleteAccount?: (password: string) => Promise<{ success: boolean }>;
  update?: (payload: UpdateAccount) => Promise<Account>;
};

const AccountDetails: React.FC<AccountDetailsProps> = ({
  id,
  hash,
  orders,
  shippingMethods,
  ordersLoading,
  account,
  changePassword,
  deleteAccount,
  update,
}) => {
  const router = useRouter();
  const { locale } = useParams();

  const { accountLoading, logout } = useContext(AccountContext);

  const { formatMessage } = useFormat({ name: 'gruenland' });

  const handleLogout = useCallback(() => {
    logout().then(() => router.push(`/${locale}`));
  }, [locale, logout, router]);

  const tabs = useMemo<AccountTab[]>(() => {
    return [
      {
        name: formatMessage({ id: 'privatearea.menu.dashboard', defaultMessage: 'Area personale' }),
        href: '?',
        isActive: hash === '',
      },
      {
        name: formatMessage({ id: 'privatearea.menu.profile', defaultMessage: 'Profilo' }),
        href: '?hash=profile',
        isActive: hash === 'profile',
      },
      {
        name: formatMessage({ id: 'privatearea.menu.addresses', defaultMessage: 'Indirizzi' }),
        href: '?hash=addresses',
        isActive: hash === 'addresses',
      },
      {
        name: formatMessage({ id: 'privatearea.menu.orders', defaultMessage: 'Ordini' }),
        href: '?hash=orders',
        isActive: hash === 'orders',
      },
    ].map(({ href, ...rest }) => ({
      ...rest,
      href: toAccountUrl(href),
    }));
  }, [formatMessage, hash]);

  const accountPagesRef = useMemo(() => {
    return {
      'edit-personal-info': <PersonalInfoForm account={account} update={update} />,
      'change-password': <ChangePasswordForm changePassword={changePassword} />,
      'delete-account': <DeleteAccountForm deleteAccount={deleteAccount} />,
    };
  }, [account, changePassword, deleteAccount, update]);

  const AccountSection = useMemo(
    () => accountPagesRef[id as keyof typeof accountPagesRef] ?? <MyAccount />,
    [accountPagesRef, id],
  );

  const addresses = useMemo(() => {
    return id?.startsWith('address') ? (
      <AddressForm editedAddressId={(id?.startsWith('address_') && id.slice('address_'.length)) || undefined} />
    ) : (
      <Addresses />
    );
  }, [id]);

  const OrdersSection = useMemo(() => {
    const order = orders.find((o) => o.orderId === id?.slice('order_'.length)) as Order;
    return id && id.startsWith('order') ? (
      <>
        <OrderPage order={order} shippingMethods={shippingMethods} />
        <OrderReturnButton order={order} />
      </>
    ) : (
      <Orders orders={orders} loading={ordersLoading} />
    );
  }, [id, orders, ordersLoading, shippingMethods]);

  const mapping = {
    '': <DashboardSection tabs={tabs.slice(1)} />,
    profile: AccountSection,
    addresses: addresses,
    orders: OrdersSection,
  };

  const Content = mapping[hash as keyof typeof mapping];

  const tabButtonClassNames = classNames('whitespace-nowrap', { 'cursor-default': accountLoading });

  const tabButtonLabelClassNames = useCallback((tab: AccountTab) => {
    return `hover:underline ${tab.isActive ? 'text-primary-black font-medium' : 'text-secondary-black'}`;
  }, []);

  return (
    <div className="flex">
      <div className="hidden w-1/4 flex-col gap-9 px-[56px] pt-11 md:flex">
        {tabs.map((tab) => (
          <Link link={accountLoading ? '' : tab.href} key={tab.name} className={tabButtonClassNames}>
            {accountLoading ? (
              <Skeleton />
            ) : (
              <Typography className={tabButtonLabelClassNames(tab)}>{tab.name}</Typography>
            )}
          </Link>
        ))}

        {accountLoading ? (
          <Skeleton className="h-[30px]" />
        ) : (
          <Button onClick={handleLogout} variant="buttonSecondaryNegative">
            {formatMessage({ id: 'user.signOut', defaultMessage: 'Esci' })}
          </Button>
        )}
      </div>
      <div className="flex w-full flex-col border-neutral-400 md:border-l lg:items-start">
        <div className="w-full pb-[48px]">
          <div key={hash} className="p-6 md:p-10">
            {Content}
          </div>
        </div>
      </div>
    </div>
  );
};
export default AccountDetails;
